/* eslint-disable no-mixed-spaces-and-tabs */
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import {
	CHANGE_DRIVER,
	GET_DRIVERS,
	GET_REQUEST_INFO,
	setDriversAction,
	setIsDriverChangingAction,
	setIsDriversLoadingAction,
	setIsRequestInfoLoadingAction,
	setRequestInfoAction,
} from './actions';
import { FORM_NAME, REQUEST_ERROR_MESSAGE, REQUIRED_FIELDS } from './constants';
import { transformToValidationErrors } from './utils';
import { DISMISS_TIME } from '../../api/constants';
import { getRequestInfoRequest } from '../../api/reqs-requests';
import { getDataForSaveSelector, getFormIsInvalidSelector } from './selectors';
import { getRequestDriversRequest, updateMyReqWithPayloadRequest } from '../../api/requests';
import { getRawPaginationSelector, getReqsSelector } from '../reqs-list/selectors';
import { setPaginationAction, setReqsAction } from '../reqs-list/actions';
import { errorHandler } from '../../api/utils';
import { removeModalQuery } from '../../utils/removeModalQuery';

export function* changeDriverSaga({ payload: { redirect, id, searchQuery } }) {
	try {
		yield put(setIsDriverChangingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForRequest = yield select(getDataForSaveSelector());

		const { data, message, toast, errors } = yield call(
			updateMyReqWithPayloadRequest,
			id,
			'/setDriver',
			dataForRequest,
		);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link
							? {
									link: `${toast?.link}${
										removeModalQuery(searchQuery) ? `&${removeModalQuery(searchQuery)}` : ''
									}`,
							  }
							: {}),
					}),
				);
			}

			const reqs = yield select(getReqsSelector());

			let newReqs = [];

			if (data.routes.includes(window.location.pathname)) {
				const updatedReq = {
					...data,
					link: `?${searchQuery ? `${searchQuery}&` : ''}modal=reqs_details&id=${data.id}`,
					additionalPoints: data.points.length - 2,
					renderPoints: [data.points[0], data.points.at(-1)],
				};

				newReqs = reqs.map(req => {
					return req.id === data.id ? updatedReq : req;
				});
			} else {
				newReqs = reqs.filter(req => {
					return req.id !== data.id;
				});

				const pagination = yield select(getRawPaginationSelector());

				if (pagination) {
					yield put(setPaginationAction({ ...pagination, total: pagination.total - 1 }));
				}
			}

			yield put(setReqsAction(newReqs));

			redirect();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...REQUIRED_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsDriverChangingAction(false));
	}
}

export function* getRequestInfoSaga({ payload: id }) {
	try {
		yield put(setIsRequestInfoLoadingAction(true));

		const { data, message, toast } = yield call(getRequestInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setRequestInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsRequestInfoLoadingAction(false));
	}
}

export function* getDriversSaga({ payload }) {
	try {
		yield put(setIsDriversLoadingAction(true));

		const { data, message, toast } = yield call(getRequestDriversRequest, payload);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setDriversAction(
					data.map(driver => ({
						key: driver.id,
						value: driver.id,
						text: driver.name,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsDriversLoadingAction(false));
	}
}

export default function* driverChangeModalSaga() {
	yield takeEvery(CHANGE_DRIVER, changeDriverSaga);
	yield takeEvery(GET_REQUEST_INFO, getRequestInfoSaga);
	yield takeEvery(GET_DRIVERS, getDriversSaga);
}
